<script>
  import Layout from '../components/Layout.svelte';
  import { onMount } from 'svelte';
  import { user as userStore } from '../stores/authStore.js';
  import { provinceOptions, splitCountryValue } from '../lib/canadaTax.js';

  const caProvinceOptions = provinceOptions();

  let user = null;
  let loading = false;
  let error = null;
  let success = null;

  // Form fields
  let formData = {
    username: '',
    password: '',
    jobtreadApiKey: '',
    jobtreadOrgId: '',
    xeroInvoiceCode: '',
    xeroExpenseCode: '',
    country: 'NZ'
  };

  let showPassword = false;
  let jobtreadConnectionStatus = null; // null, 'checking', 'success', 'error'
  let jobtreadOrgName = '';
  let formValid = false;
  let availableOrganizations = [];
  let fetchingOrganizations = false;
  let showManualOrgId = false;

  // User list variables
  let allUsers = [];
  let loadingUsers = false;
  let userListError = null;

  // Sorting variables (default: sort by id ascending)
  let sortColumn = 'id'; // 'id' | 'email' | 'subStatus' | null
  let sortDirection = 'asc'; // 'asc' | 'desc' | null

  function cycleSort(column) {
    if (sortColumn !== column) {
      sortColumn = column;
      sortDirection = 'asc';
    } else if (sortDirection === 'asc') {
      sortDirection = 'desc';
    } else {
      // Was descending → back to no sort
      sortColumn = null;
      sortDirection = null;
    }
  }

  function getSortValue(userItem, column) {
    if (column === 'id') return userItem.id || 0;
    if (column === 'email') return (userItem.username || '').toLowerCase();
    if (column === 'subStatus') return (userItem.subscription?.status || 'trial').toLowerCase();
    return '';
  }

  // Trial filter buttons (single-select toggle)
  let activeFilter = null; // 'trial' | 'noLogin5d' | 'lt10days' | 'expired' | null

  function toggleFilter(name) {
    activeFilter = activeFilter === name ? null : name;
  }

  function isTrial(userItem) {
    const sub = userItem.subscription || {};
    return !sub.status || sub.status === 'trial';
  }

  function daysUntilTrialEnd(userItem) {
    const sub = userItem.subscription || {};
    if (!sub.trialEndDate) return null;
    const end = new Date(sub.trialEndDate);
    if (isNaN(end.getTime())) return null;
    return Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
  }

  // An "active" trial has an armed end date that is still in the future
  // (excludes perpetual trials with no end date and already-expired trials).
  function isActiveTrial(userItem) {
    if (!isTrial(userItem)) return false;
    const daysLeft = daysUntilTrialEnd(userItem);
    return daysLeft !== null && daysLeft > 0;
  }

  function isExpiredTrial(userItem) {
    if (!isTrial(userItem)) return false;
    const daysLeft = daysUntilTrialEnd(userItem);
    return daysLeft !== null && daysLeft <= 0;
  }

  function matchesFilter(userItem) {
    if (!activeFilter) return true;

    if (activeFilter === 'expired') return isExpiredTrial(userItem);

    // Remaining filters only apply to active armed trials.
    if (!isActiveTrial(userItem)) return false;

    if (activeFilter === 'trial') return true;

    if (activeFilter === 'noLogin5d') {
      if (!userItem.lastLogin) return true; // never logged in
      const days = (Date.now() - new Date(userItem.lastLogin).getTime()) / (1000 * 60 * 60 * 24);
      return days > 5;
    }

    if (activeFilter === 'lt10days') {
      const daysLeft = daysUntilTrialEnd(userItem);
      return daysLeft !== null && daysLeft < 10; // active trials only, so daysLeft > 0
    }

    return true;
  }

  // Reference activeFilter so Svelte re-runs this when the filter changes
  $: filteredUsers = (activeFilter, allUsers).filter(matchesFilter);

  $: sortedUsers = (!sortColumn || !sortDirection)
    ? filteredUsers
    : [...filteredUsers].sort((a, b) => {
        const dir = sortDirection === 'asc' ? 1 : -1;
        const va = getSortValue(a, sortColumn);
        const vb = getSortValue(b, sortColumn);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return (a.id || 0) - (b.id || 0);
      });

  // Delete user variables
  let showDeleteModal = false;
  let userToDelete = null;
  let deletePassword = '';
  let deleteLoading = false;
  let deleteError = null;

  // Extend trial variables
  let extendTrialLoadingId = null;

  // Admin notes variables
  let savingNotesId = null;
  let savedNotesId = null;

  // Svelte action: auto-grow a textarea to fit its content so all text is shown
  function autoResize(node) {
    const resize = () => {
      node.style.height = 'auto';
      node.style.height = `${node.scrollHeight}px`;
    };
    resize();
    node.addEventListener('input', resize);
    return {
      destroy() {
        node.removeEventListener('input', resize);
      }
    };
  }

  async function saveNotes(userItem, value) {
    const newNotes = value ?? '';
    if ((userItem.adminNotes || '') === newNotes) return; // no change

    savingNotesId = userItem.id;
    savedNotesId = null;
    try {
      const response = await fetch(`/api/users/${userItem.id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ notes: newNotes })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        userItem.adminNotes = data.adminNotes;
        allUsers = allUsers; // trigger reactivity
        savedNotesId = userItem.id;
        setTimeout(() => { if (savedNotesId === userItem.id) savedNotesId = null; }, 2000);
      } else {
        error = data.error || 'Failed to save notes.';
      }
    } catch (err) {
      console.error('Save notes failed:', err);
      error = 'Failed to save notes.';
    } finally {
      savingNotesId = null;
    }
  }

  // Arm trial variables
  let showArmTrialModal = false;
  let armTrialTarget = null; // user object
  let armTrialDate = '';
  let armTrialWarningDays = 14;
  let armTrialLoading = false;
  let armTrialError = null;

  // Subscribe to auth store
  userStore.subscribe(value => {
    user = value;
  });

  async function fetchAllUsers() {
    loadingUsers = true;
    userListError = null;
    
    try {
      console.log('🔍 Fetching users from /api/users/list');
      const response = await fetch('/api/users/list');
      
      console.log('📡 Response status:', response.status);
      console.log('📡 Response headers:', response.headers.get('content-type'));
      
      if (response.ok) {
        const responseText = await response.text();
        console.log('📄 Raw response:', responseText.substring(0, 200) + '...');
        
        try {
          const data = JSON.parse(responseText);
          if (data.success && Array.isArray(data.users)) {
            allUsers = data.users;
            console.log('✅ Successfully loaded', data.users.length, 'users');
          } else {
            console.error('❌ Invalid response format:', data);
            userListError = 'Invalid response format from server';
          }
        } catch (parseError) {
          console.error('❌ JSON parse error:', parseError);
          console.error('❌ Response was:', responseText);
          userListError = 'Server returned invalid JSON';
        }
      } else {
        const errorText = await response.text();
        console.error('❌ HTTP error:', response.status, errorText);
        userListError = `Server error: ${response.status}`;
      }
    } catch (err) {
      console.error('❌ Network error fetching users:', err);
      userListError = 'Network error loading users';
    } finally {
      loadingUsers = false;
    }
  }

  onMount(async () => {
    // Fetch current user info to check permissions
    try {
      const response = await fetch('/api/user');
      
      if (response.ok) {
        const userData = await response.json();
        if (userData.success && userData.user) {
          user = userData.user;
          console.log('ManageUsers - User loaded:', user); // Debug log
          // Check if user has permission to manage users
          if (user.id !== 1 && user.id !== 2) {
            error = 'You do not have permission to manage users.';
            return;
          }
          
          // Fetch all users if permission check passes
          await fetchAllUsers();
        } else {
          error = 'Unable to verify user permissions.';
        }
      } else {
        error = 'Authentication failed. Please log in again.';
      }
    } catch (err) {
      console.error('Error fetching user info:', err);
      error = 'Unable to verify user permissions.';
    }
  });

  // Validate form - JobTread fields are now optional
  $: {
    formValid = formData.username.trim() !== '' &&
                formData.password.trim() !== '' &&
                formData.xeroInvoiceCode.trim() !== '' &&
                formData.xeroExpenseCode.trim() !== '' &&
                formData.country.trim() !== '';
  }

  function togglePasswordVisibility() {
    showPassword = !showPassword;
  }

  async function fetchOrganizations() {
    if (!formData.jobtreadApiKey.trim()) {
      error = 'Please enter JobTread API Key first.';
      return;
    }

    fetchingOrganizations = true;
    error = null;
    availableOrganizations = [];

    try {
      const response = await fetch('/api/users/fetch-jobtread-organizations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: formData.jobtreadApiKey })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        availableOrganizations = data.organizations;
        
        if (availableOrganizations.length === 1) {
          // Auto-select if only one organization
          formData.jobtreadOrgId = availableOrganizations[0].id;
          jobtreadOrgName = availableOrganizations[0].name;
        } else if (availableOrganizations.length === 0) {
          error = 'No organizations found for this API key.';
          showManualOrgId = true;
        }
      } else {
        error = data.error || 'Failed to fetch organizations';
        showManualOrgId = true;
      }
    } catch (err) {
      console.error('Failed to fetch organizations:', err);
      error = 'Failed to fetch organizations. You can enter the Organisation ID manually.';
      showManualOrgId = true;
    } finally {
      fetchingOrganizations = false;
    }
  }

  async function checkJobTreadConnection() {
    if (!formData.jobtreadApiKey.trim() || !formData.jobtreadOrgId.trim()) {
      error = 'Please enter JobTread API Key and Organisation ID first.';
      return;
    }

    jobtreadConnectionStatus = 'checking';
    error = null;

    try {
      const response = await fetch('/api/users/validate-jobtread', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: formData.jobtreadApiKey,
          orgId: formData.jobtreadOrgId
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        jobtreadConnectionStatus = 'success';
        jobtreadOrgName = data.organization.name;
        // Don't set success message here - it's shown below the button
      } else {
        jobtreadConnectionStatus = 'error';
        error = data.error || 'Failed to connect to JobTread';
      }
    } catch (err) {
      console.error('JobTread connection check failed:', err);
      jobtreadConnectionStatus = 'error';
      error = 'Failed to check JobTread connection';
    }
  }

  async function createUser() {
    if (!formValid) {
      error = 'Please fill in all required fields.';
      return;
    }

    loading = true;
    error = null;
    success = null;

    try {
      const response = await fetch('/api/users/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        // The picker holds 'CA-BC' for Canadian provinces; send country + province.
        body: JSON.stringify({ ...formData, ...splitCountryValue(formData.country) })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        success = `✅ User "${formData.username}" created successfully!`;
        
        // Reset form
        formData = {
          username: '',
          password: '',
          jobtreadApiKey: '',
          jobtreadOrgId: '',
          xeroInvoiceCode: '',
          xeroExpenseCode: '',
          country: 'NZ'
        };
        jobtreadConnectionStatus = null;
        jobtreadOrgName = '';
        
        // Refresh the user list
        await fetchAllUsers();
      } else {
        error = data.error || 'Failed to create user';
      }
    } catch (err) {
      console.error('User creation failed:', err);
      error = 'Failed to create user';
    } finally {
      loading = false;
    }
  }

  async function extendTrial(userItem) {
    extendTrialLoadingId = userItem.id;
    error = null;
    success = null;
    try {
      const response = await fetch(`/api/users/${userItem.id}/extend-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok && data.success) {
        const newEnd = new Date(data.trialEndDate).toLocaleDateString();
        success = `Trial extended for "${userItem.username}" — new end date: ${newEnd}`;
        await fetchAllUsers();
      } else {
        error = data.error || 'Failed to extend trial.';
      }
    } catch (err) {
      console.error('Extend trial failed:', err);
      error = 'Failed to extend trial.';
    } finally {
      extendTrialLoadingId = null;
    }
  }

  function isTrialUser(userItem) {
    const sub = userItem.subscription || {};
    return !sub.status || sub.status === 'trial';
  }

  function openDeleteModal(userItem) {
    userToDelete = userItem;
    deletePassword = '';
    deleteError = null;
    showDeleteModal = true;
  }

  function closeDeleteModal() {
    showDeleteModal = false;
    userToDelete = null;
    deletePassword = '';
    deleteError = null;
    deleteLoading = false;
  }

  function getSubscriptionLabel(userItem) {
    const sub = userItem.subscription || {};
    const status = sub.status || 'trial';
    if (status === 'active') return { text: 'Active', cls: 'bg-green-100 text-green-800' };
    if (status === 'past_due') return { text: 'Past Due', cls: 'bg-red-100 text-red-800' };
    if (status === 'canceled' || status === 'cancelled') return { text: 'Cancelled', cls: 'bg-gray-100 text-gray-800' };
    if (status === 'trial') {
      if (!sub.trialEndDate) return { text: 'Perpetual Trial', cls: 'bg-blue-100 text-blue-800' };
      const end = new Date(sub.trialEndDate);
      const now = new Date();
      if (end > now) {
        const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
        return { text: `Trial (${days}d left)`, cls: 'bg-yellow-100 text-yellow-800' };
      }
      return { text: 'Trial Expired', cls: 'bg-red-100 text-red-800' };
    }
    return { text: status, cls: 'bg-gray-100 text-gray-800' };
  }

  function isPerpetualTrial(userItem) {
    const sub = userItem.subscription || {};
    return (!sub.status || sub.status === 'trial') && !sub.trialEndDate;
  }

  function openArmTrialModal(target) {
    armTrialTarget = target;
    const defaultDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    armTrialDate = defaultDate.toISOString().split('T')[0];
    armTrialWarningDays = 14;
    armTrialError = null;
    showArmTrialModal = true;
  }

  function closeArmTrialModal() {
    showArmTrialModal = false;
    armTrialTarget = null;
    armTrialDate = '';
    armTrialError = null;
  }

  async function confirmArmTrial() {
    if (!armTrialDate) {
      armTrialError = 'Please select a trial end date.';
      return;
    }
    armTrialLoading = true;
    armTrialError = null;

    try {
      const response = await fetch(`/api/users/${armTrialTarget.id}/arm-trial`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          trialEndDate: new Date(armTrialDate).toISOString(),
          warningDays: armTrialWarningDays
        })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        success = `✅ Trial armed for "${armTrialTarget.username}".`;
        closeArmTrialModal();
        await fetchAllUsers();
      } else {
        armTrialError = data.error || 'Failed to arm trial.';
      }
    } catch (err) {
      console.error('Arm trial failed:', err);
      armTrialError = 'Failed to arm trial.';
    } finally {
      armTrialLoading = false;
    }
  }

  async function confirmDeleteUser() {
    if (!deletePassword.trim()) {
      deleteError = 'Please enter your password to confirm deletion.';
      return;
    }

    if (!userToDelete) {
      deleteError = 'No user selected for deletion.';
      return;
    }

    deleteLoading = true;
    deleteError = null;

    try {
      const response = await fetch('/api/users/delete', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userIdToDelete: userToDelete.id,
          confirmationPassword: deletePassword
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        success = `✅ User "${userToDelete.username}" has been deleted successfully.`;
        closeDeleteModal();
        
        // Refresh the user list
        await fetchAllUsers();
      } else {
        deleteError = data.error || 'Failed to delete user';
      }
    } catch (err) {
      console.error('User deletion failed:', err);
      deleteError = 'Failed to delete user';
    } finally {
      deleteLoading = false;
    }
  }
</script>

<Layout activeRoute="manage-users">
  <div class="container mx-auto px-4 py-8">
    <h1 class="text-2xl font-bold mb-6">Manage Users</h1>
    
    {#if user && (user.id === 1 || user.id === 2)}
      <!-- Existing Users List -->
      <div class="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h2 class="text-xl font-semibold">Existing Users</h2>
          </div>

          <!-- Trial filter buttons (single-select) -->
          <div class="flex flex-wrap items-center gap-2 mb-4">
            <button
              type="button"
              on:click={() => toggleFilter('trial')}
              class="px-3 py-1.5 border text-sm font-medium rounded-md transition-colors {activeFilter === 'trial' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}"
            >
              Users on Trial
            </button>
            <button
              type="button"
              on:click={() => toggleFilter('noLogin5d')}
              class="px-3 py-1.5 border text-sm font-medium rounded-md transition-colors {activeFilter === 'noLogin5d' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}"
            >
              Trial with no login past 5 days
            </button>
            <button
              type="button"
              on:click={() => toggleFilter('lt10days')}
              class="px-3 py-1.5 border text-sm font-medium rounded-md transition-colors {activeFilter === 'lt10days' ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}"
            >
              Trial Users &lt; 10 days left
            </button>
            <button
              type="button"
              on:click={() => toggleFilter('expired')}
              class="px-3 py-1.5 border text-sm font-medium rounded-md transition-colors {activeFilter === 'expired' ? 'bg-red-600 border-red-600 text-white' : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'}"
            >
              Expired Trials
            </button>
          </div>

          {#if loadingUsers}
            <div class="flex justify-center items-center py-4">
              <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
              <span class="ml-2 text-gray-600">Loading users...</span>
            </div>
          {:else if userListError}
            <div class="p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {userListError}
            </div>
          {:else if allUsers.length === 0}
            <p class="text-gray-500 text-center py-4">No users found.</p>
          {:else}
            <div class="max-h-[32rem] overflow-y-auto border border-gray-200 rounded">
              <table class="min-w-full divide-y divide-[#edf0f7]">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button type="button" on:click={() => cycleSort('id')} class="group inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-600 focus:outline-none">
                        <span>ID</span>
                        <span class="text-[10px] {sortColumn === 'id' ? 'text-gray-600' : 'text-gray-300'}">
                          {sortColumn === 'id' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button type="button" on:click={() => cycleSort('email')} class="group inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-600 focus:outline-none">
                        <span>Email</span>
                        <span class="text-[10px] {sortColumn === 'email' ? 'text-gray-600' : 'text-gray-300'}">
                          {sortColumn === 'email' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Country</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Created</th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Last Login</th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      <button type="button" on:click={() => cycleSort('subStatus')} class="group inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-600 focus:outline-none">
                        <span>Subscription</span>
                        <span class="text-[10px] {sortColumn === 'subStatus' ? 'text-gray-600' : 'text-gray-300'}">
                          {sortColumn === 'subStatus' ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                        </span>
                      </button>
                    </th>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Notes</th>
                    <th class="px-3 py-2 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-[#edf0f7]">
                  {#each sortedUsers as userItem (userItem.id)}
                    <tr class="hover:bg-[#f0f4ff]">
                      <td class="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                        {userItem.id}
                      </td>
                      <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {userItem.username}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                        {userItem.country || 'N/A'}
                      </td>
                      <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {userItem.createdAt ? new Date(userItem.createdAt).toLocaleDateString() : 'N/A'}
                      </td>
                      <td class="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                        {#if userItem.lastLogin}
                          <div class="flex items-center">
                            <div class="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                            <div>
                              <div class="text-green-600 text-sm">
                                {new Date(userItem.lastLogin).toLocaleDateString()}
                              </div>
                              <div class="text-xs text-gray-400">
                                {new Date(userItem.lastLogin).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                              </div>
                            </div>
                          </div>
                        {:else}
                          <div class="flex items-center">
                            <div class="flex-shrink-0 w-2 h-2 bg-gray-300 rounded-full mr-2"></div>
                            <span class="text-gray-400 italic">Never logged in</span>
                          </div>
                        {/if}
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm">
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium {getSubscriptionLabel(userItem).cls}">
                          {getSubscriptionLabel(userItem).text}
                        </span>
                      </td>
                      <td class="px-4 py-2 text-sm align-top">
                        <div class="relative">
                          <textarea
                            rows="1"
                            use:autoResize
                            value={userItem.adminNotes || ''}
                            on:blur={(e) => saveNotes(userItem, e.target.value)}
                            placeholder="Add note..."
                            class="w-48 px-2 py-1 border border-gray-200 rounded text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-400 focus:border-indigo-400 resize-none overflow-hidden"
                          ></textarea>
                          {#if savingNotesId === userItem.id}
                            <span class="text-[10px] text-gray-400">Saving…</span>
                          {:else if savedNotesId === userItem.id}
                            <span class="text-[10px] text-green-500">Saved</span>
                          {/if}
                        </div>
                      </td>
                      <td class="px-3 py-2 whitespace-nowrap text-sm">
                        <div class="flex items-center gap-1">
                          {#if isTrialUser(userItem)}
                            <button
                              on:click={() => extendTrial(userItem)}
                              disabled={extendTrialLoadingId === userItem.id}
                              class="text-green-600 hover:text-green-800 hover:bg-green-50 px-1.5 py-0.5 rounded transition-colors text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Extend trial by 7 days"
                            >
                              {#if extendTrialLoadingId === userItem.id}
                                <div class="animate-spin rounded-full h-3 w-3 border-b-2 border-green-600"></div>
                              {:else}
                                +7d
                              {/if}
                            </button>
                          {/if}
                          {#if isPerpetualTrial(userItem)}
                            <button
                              on:click={() => openArmTrialModal(userItem)}
                              class="text-orange-600 hover:text-orange-800 hover:bg-orange-50 p-1 rounded transition-colors"
                              title="Arm trial end date"
                            >
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                              </svg>
                            </button>
                          {/if}
                          {#if userItem.id !== user?.id}
                            <button
                              on:click={() => openDeleteModal(userItem)}
                              class="text-red-600 hover:text-red-800 hover:bg-red-50 p-1 rounded transition-colors"
                              title="Delete user"
                            >
                              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                            </button>
                          {:else}
                            <span class="text-gray-400 text-xs">You</span>
                          {/if}
                        </div>
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
            <p class="text-sm text-gray-500 mt-2">
              {#if activeFilter}
                Showing {filteredUsers.length} of {allUsers.length} users
              {:else}
                Total users: {allUsers.length}
              {/if}
            </p>
          {/if}
        </div>
      </div>

      <!-- Create New User section -->
      <div class="bg-white rounded-lg shadow overflow-hidden">
        <div class="p-6">
          <h2 class="text-xl font-semibold mb-6">Create New User</h2>
          
          {#if error}
            <div class="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          {/if}
          
          {#if success}
            <div class="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
              {success}
            </div>
          {/if}

          <form on:submit|preventDefault={createUser} class="space-y-6">
            <!-- Username -->
            <div>
              <label for="username" class="block text-sm font-medium text-gray-700 mb-2">
                Username (Email) *
              </label>
              <input
                type="email"
                id="username"
                bind:value={formData.username}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="user@example.com"
                required
              />
            </div>

            <!-- Password -->
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <div class="relative">
{#if showPassword}
                  <input
                    type="text"
                    id="password"
                    bind:value={formData.password}
                    class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter password"
                    required
                  />
                {:else}
                  <input
                    type="password"
                    id="password"
                    bind:value={formData.password}
                    class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter password"
                    required
                  />
                {/if}
                <button
                  type="button"
                  on:click={togglePasswordVisibility}
                  class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            <!-- JobTread Section -->
            <div class="border-t pt-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">JobTread Integration</h3>
              
              <!-- Note for JobTread Setup -->
              <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p class="text-sm text-blue-800 mb-2">
                  📖 <strong>Setup JobTread Integration:</strong>
                </p>
                <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1 mb-3">
                  <li><strong>Step 1:</strong> Go to <a href="https://app.jobtread.com/settings/integrations/api/grants" target="_blank" class="text-blue-600 underline hover:text-blue-800">API Grants</a></li>
                  <li><strong>Step 2:</strong> Click "Add Grant" and name it <strong>Buildzilla</strong></li>
                  <li><strong>Step 3:</strong> Copy the grant key and paste it below</li>
                  <li><strong>Step 4:</strong> Click "Fetch Organizations" to automatically load your organizations</li>
                </ol>
                <img src="/images/apigrants.jpg" alt="JobTread API Grant Example" class="w-full max-w-md mx-auto rounded border border-blue-300 shadow-sm" />
              </div>
              
              <!-- API Key Field -->
              <div class="mb-4">
                <label for="jobtreadApiKey" class="block text-sm font-medium text-gray-700 mb-2">
                  JobTread Grant Key (Optional)
                </label>
                <input
                  type="text"
                  id="jobtreadApiKey"
                  bind:value={formData.jobtreadApiKey}
                  on:input={() => { availableOrganizations = []; formData.jobtreadOrgId = ''; }}
                  class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter Grant Key"
                />
                
                <button
                  type="button"
                  on:click={fetchOrganizations}
                  disabled={fetchingOrganizations || !formData.jobtreadApiKey.trim()}
                  class="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {#if fetchingOrganizations}
                    <span class="flex items-center">
                      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Fetching...
                    </span>
                  {:else}
                    Fetch Organizations
                  {/if}
                </button>
              </div>

              <!-- Organization Selection Dropdown -->
              {#if availableOrganizations.length > 1}
                <div class="mb-4">
                  <label for="jobtreadOrgSelect" class="block text-sm font-medium text-gray-700 mb-2">
                    Select Organization (Optional)
                  </label>
                  <select
                    id="jobtreadOrgSelect"
                    bind:value={formData.jobtreadOrgId}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">-- Select an Organization --</option>
                    {#each availableOrganizations as org}
                      <option value={org.id}>{org.name}</option>
                    {/each}
                  </select>
                </div>
              {/if}

              <!-- Manual Organisation ID (fallback) -->
              {#if showManualOrgId || (formData.jobtreadOrgId && availableOrganizations.length === 0)}
                <div class="mb-4">
                  <label for="jobtreadOrgId" class="block text-sm font-medium text-gray-700 mb-2">
                    Organisation ID (Manual Entry - Optional)
                  </label>
                  <input
                    type="text"
                    id="jobtreadOrgId"
                    bind:value={formData.jobtreadOrgId}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="Enter Organisation ID"
                  />
                </div>
              {/if}

              <!-- Show selected org confirmation -->
              {#if formData.jobtreadOrgId && availableOrganizations.length === 1}
                <div class="mb-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p class="text-sm text-green-700">
                    Organization: <strong>{jobtreadOrgName}</strong>
                  </p>
                </div>
              {/if}

              <div class="mt-4">
                <button
                  type="button"
                  on:click={checkJobTreadConnection}
                  disabled={jobtreadConnectionStatus === 'checking' || !formData.jobtreadApiKey.trim() || !formData.jobtreadOrgId.trim()}
                  class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {#if jobtreadConnectionStatus === 'checking'}
                    <span class="flex items-center">
                      <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Checking Connection...
                    </span>
                  {:else}
                    Check JobTread Connection
                  {/if}
                </button>

                {#if jobtreadConnectionStatus === 'success'}
                  <div class="mt-2 text-sm text-green-600">
                    ✅ Connected to: {jobtreadOrgName}
                  </div>
                {:else if jobtreadConnectionStatus === 'error'}
                  <div class="mt-2 text-sm text-red-600">
                    ❌ Connection failed
                  </div>
                {/if}
              </div>
            </div>

            <!-- Xero Section -->
            <div class="border-t pt-6">
              <h3 class="text-lg font-medium text-gray-900 mb-4">Xero Integration</h3>
              <div class="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p class="text-sm text-blue-800 mb-2">
                  📖 <strong>Setup Xero Account Codes:</strong>
                </p>
                <ol class="text-sm text-blue-800 list-decimal list-inside space-y-1 mb-3">
                  <li>These codes are used for catch all defaults when syncing to Xero.</li>
                  <li>Please refer to Xero->Accounting->Accounting Settings->Chart of Accounts &nbsp; <a href="https://xero.com" target="_blank" class="text-blue-600 underline hover:text-blue-800">https://xero.com</a></li>
                  
                </ol>
                
              </div>
              
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label for="xeroInvoiceCode" class="block text-sm font-medium text-gray-700 mb-2">
                    Default Xero Sales Invoice Code *
                  </label>
                  <input
                    type="text"
                    id="xeroInvoiceCode"
                    bind:value={formData.xeroInvoiceCode}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., 200"
                    required
                  />
                </div>

                <div>
                  <label for="xeroExpenseCode" class="block text-sm font-medium text-gray-700 mb-2">
                    DefaultXero Expense Code *
                  </label>
                  <input
                    type="text"
                    id="xeroExpenseCode"
                    bind:value={formData.xeroExpenseCode}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                    placeholder="e.g., 400"
                    required
                  />
                </div>
              </div>
            </div>

            <!-- Country -->
            <div>
              <label for="country" class="block text-sm font-medium text-gray-700 mb-2">
                Country *
              </label>
              <select
                id="country"
                bind:value={formData.country}
                class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                required
              >
                <option value="AU">Australia</option>
                <optgroup label="Canada">
                  {#each caProvinceOptions as p}
                    <option value={p.value}>{p.label}</option>
                  {/each}
                </optgroup>
                <option value="GB">United Kingdom (GBP)</option>
                <option value="GI">Gibraltar (GIP)</option>
                <option value="NZ">New Zealand</option>
                <option value="US">United States</option>
              </select>
            </div>

            <!-- Submit Button -->
            <div class="pt-6">
              <button
                type="submit"
                disabled={!formValid || loading}
                class="w-full bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {#if loading}
                  <span class="flex items-center justify-center">
                    <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating User...
                  </span>
                {:else}
                  Save and Create User
                {/if}
              </button>
            </div>
          </form>
        </div>
      </div>
    {:else}
      <div class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        <strong>Access Denied:</strong> You do not have permission to manage users.
      </div>
    {/if}
  </div>

  <!-- Delete User Confirmation Modal -->
  {#if showDeleteModal}
    <div class="fixed z-50 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <!-- Background overlay -->
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <!-- Modal panel -->
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                <!-- Warning icon -->
                <svg class="h-6 w-6 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Delete User
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500 mb-4">
                    Are you sure you want to delete user <strong>{userToDelete?.username}</strong>? This action cannot be undone.
                  </p>
                  
                  {#if deleteError}
                    <div class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                      {deleteError}
                    </div>
                  {/if}
                  
                  <div class="mb-4">
                    <label for="deletePassword" class="block text-sm font-medium text-gray-700 mb-2">
                      Enter your password to confirm:
                    </label>
                    <input
                      type="password"
                      id="deletePassword"
                      bind:value={deletePassword}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                      placeholder="Your password"
                      disabled={deleteLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              on:click={confirmDeleteUser}
              disabled={deleteLoading || !deletePassword.trim()}
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {#if deleteLoading}
                <span class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting...
                </span>
              {:else}
                Delete User
              {/if}
            </button>
            <button 
              type="button" 
              on:click={closeDeleteModal}
              disabled={deleteLoading}
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-[#f0f4ff] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}

  <!-- Arm Trial Modal -->
  {#if showArmTrialModal}
    <div class="fixed z-50 inset-0 overflow-y-auto">
      <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div class="fixed inset-0 transition-opacity" aria-hidden="true">
          <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>
        
        <div class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div class="sm:flex sm:items-start">
              <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg class="h-6 w-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 class="text-lg leading-6 font-medium text-gray-900">
                  Arm Trial for {armTrialTarget?.username}
                </h3>
                <div class="mt-2">
                  <p class="text-sm text-gray-500 mb-4">
                    Set a trial end date for this user. After this date they will need an active subscription to continue.
                  </p>
                  
                  {#if armTrialError}
                    <div class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                      {armTrialError}
                    </div>
                  {/if}
                  
                  <div class="mb-4">
                    <label for="armTrialDate" class="block text-sm font-medium text-gray-700 mb-1">
                      Trial End Date
                    </label>
                    <input
                      type="date"
                      id="armTrialDate"
                      bind:value={armTrialDate}
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      disabled={armTrialLoading}
                    />
                  </div>
                  
                  <div class="mb-4">
                    <label for="armTrialWarningDays" class="block text-sm font-medium text-gray-700 mb-1">
                      Warning Days (show banner this many days before expiry)
                    </label>
                    <input
                      type="number"
                      id="armTrialWarningDays"
                      bind:value={armTrialWarningDays}
                      min="1"
                      max="90"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      disabled={armTrialLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button 
              type="button" 
              on:click={confirmArmTrial}
              disabled={armTrialLoading || !armTrialDate}
              class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {#if armTrialLoading}
                <span class="flex items-center">
                  <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Arming...
                </span>
              {:else}
                Arm Trial
              {/if}
            </button>
            <button 
              type="button" 
              on:click={closeArmTrialModal}
              disabled={armTrialLoading}
              class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-[#f0f4ff] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  {/if}
</Layout>

<style>
  .container {
    max-width: 1400; /* Increased from 800px by 20% (800 * 1.2 = 960) */
  }
</style>
